'use client'

import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { CATALOG_STALE_TIME } from '@/constants/catalog'
import { searchProductsService } from '@/services/domain/catalog/ProductSearchService'

interface IUseProductSearchOptions {
    /** Espera depois da última tecla (padrão 500 ms, como o Navbar). */
    debounceMs?: number
}

/**
 * Autocomplete de produtos (Navbar e SearchBar): busca o texto depois do debounce, com cache por texto
 * (query key `['catalog-search', texto]`). Texto vazio não chama a API e devolve `[]`. O debounce começa vazio (o
 * `useDebounce` começa com o valor inicial, o que buscaria a primeira tecla na hora).
 */
export function useProductSearch(keyword: string, { debounceMs = 500 }: IUseProductSearchOptions = {}) {
    const trimmed = keyword.trim()
    const [term, setTerm] = useState('')

    useEffect(() => {
        const timer = setTimeout(() => setTerm(trimmed), debounceMs)
        return () => clearTimeout(timer)
    }, [trimmed, debounceMs])

    const query = useQuery({
        queryKey: ['catalog-search', term],
        queryFn: () => searchProductsService(term),
        enabled: term.length > 0,
        staleTime: CATALOG_STALE_TIME,
    })

    return {
        results: term ? (query.data ?? []) : [],
        /** Texto que gerou os resultados (já sem espaços e depois do debounce). */
        term,
        isSearching: term.length > 0 && query.isFetching,
        isError: query.isError,
    }
}
